import * as React from 'react';

import { StatCard } from './StatCard';

// Import decorative assets for dotted line connectors (desktop only)
import dottedLine1Src from '../../images/hero/dotted-line-1.svg';
import dottedLine2Src from '../../images/hero/dotted-line-2.svg';

export interface StatCardsProps {
  /** Whether to show the stat cards section */
  showStats?: boolean;
  /** First stat value (defaults to "125M") */
  stat1Value?: string;
  /** First stat label (defaults to "Members") */
  stat1Label?: string;
  /** Second stat value (defaults to "30+") */
  stat2Value?: string;
  /** Second stat label (defaults to "Countries") */
  stat2Label?: string;
  /** Third stat value (defaults to "65") */
  stat3Value?: string;
  /** Third stat label (defaults to "Clubs") */
  stat3Label?: string;
}

/**
 * StatCards renders a section with three stat cards.
 *
 * Both mobile and desktop position cards to straddle the gradient/white boundary.
 *
 * Desktop layout:
 * - Positioned absolutely at the wave boundary (top: 670px)
 * - Cards: 137px × 132px with 74px gap
 * - Dotted line connectors between cards
 *
 * Mobile layout:
 * - Sits in normal flow below the Hero CTA so the cards never overlap the button
 * - pt-[74px] = 34px gap from the CTA + 40px internal padding to the cards' visual top
 *   (matches Figma mobile Hero frame 171:2483)
 * - Cards: 102.75px × 99px with 16px gap
 * - Cards still straddle the 612px gradient (their visual top sits at section y≈557px)
 * - Horizontal row, no connectors
 *
 * Only renders when showStats is true.
 */
export const StatCards: React.FC<StatCardsProps> = ({
  showStats,
  stat1Value = '125M',
  stat1Label = 'Members',
  stat2Value = '30+',
  stat2Label = 'Countries',
  stat3Value = '65',
  stat3Label = 'Clubs',
}) => {
  // Only render when showStats is explicitly true
  if (!showStats) {
    return null;
  }

  return (
    <>
      {/* Mobile layout - in normal flow below the Hero CTA */}
      <div
        className="relative z-20 desktop:hidden px-4 pt-[74px] pb-10"
        data-testid="stat-cards-mobile"
      >
        <div className="flex gap-4 items-center justify-center w-full">
          <StatCard value={stat1Value} label={stat1Label} variant="mobile" size="small" />
          <StatCard value={stat2Value} label={stat2Label} variant="mobile" size="medium" />
          <StatCard value={stat3Value} label={stat3Label} variant="mobile" size="large" />
        </div>
      </div>

      {/* Desktop layout - absolute positioning with dotted line connectors */}
      <div
        className="hidden desktop:block absolute z-20"
        style={{
          left: '50%',
          transform: 'translateX(-50%)',
          top: '670px',
        }}
        data-testid="stat-cards-desktop"
      >
        {/* Data area: 559px × 231px, contains cards at top:50px and dotted lines */}
        <div className="relative" style={{ width: '559px', height: '231px' }}>
          {/* Dotted line 1 - curves from card 1 bottom toward card 2 */}
          <div
            className="absolute z-0 pointer-events-none select-none flex items-center justify-center"
            aria-hidden="true"
            style={{
              left: '100px',
              top: '104.29px',
              width: '107.38px',
              height: '123.46px',
              scale: '1',
              rotate: '50deg',
            }}
          >
            <div style={{ transform: 'scaleY(-1) rotate(21.09deg)' }}>
              <img src={dottedLine1Src} alt="" style={{ width: '75.24px', height: '103.31px' }} />
            </div>
          </div>

          {/* Dotted line 2 - curves from top between cards 2 and 3 */}
          <div
            className="absolute z-0 pointer-events-none select-none flex items-center justify-center"
            aria-hidden="true"
            style={{
              left: '323.92px',
              top: '0',
              width: '122.39px',
              height: '96.82px',
            }}
          >
            <div style={{ transform: 'rotate(19.14deg)' }}>
              <img src={dottedLine2Src} alt="" style={{ width: '106.85px', height: '65.41px' }} />
            </div>
          </div>

          {/* Cards container - positioned at top:50px within data area, 74px gap */}
          <div className="absolute flex items-center gap-[74px]" style={{ left: '0', top: '50px' }}>
            <StatCard value={stat1Value} label={stat1Label} variant="desktop" size="small" />
            <StatCard value={stat2Value} label={stat2Label} variant="desktop" size="medium" />
            <StatCard value={stat3Value} label={stat3Label} variant="desktop" size="large" />
          </div>
        </div>
      </div>
    </>
  );
};
